import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {ProvinceState} from "@/modules/regionsv2/province/state";
import {IdMapper} from "@/interfaces/state/base";
import {Province} from "@/interfaces/entity/province";
import {ChangeStatusProp, PaginationMeta} from "@/interfaces/common";
import {ProvinceParams} from "@/modules/regionsv2/province/params";
import {
    createProvinceV21, deleteProvinceV21,
    editProvinceV21, listProvinceV21,
} from "@/modules/regionsv2/province/services";
import {union, cloneDeep, set} from "lodash";
import {listDistrictV21} from "@/modules/regionsv2/district/services";

@Module({ dynamic: true, name: "province", store })
class ProvinceModule extends VuexModule implements ProvinceState {
    data: IdMapper<Province> = {};
    ids: number[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: ProvinceParams = new ProvinceParams();
    mapIdsByCountryId: IdMapper<Array<number>> = {};
    cachedData: IdMapper<Province> = {};
    loadingStatus: IdMapper<LoadingStatus> = {};

    get provinces() {
        return this.ids.map((id) => this.data[id]);
    }

    @Mutation
    private MAP_PROVINCES_V21(payload: any) {
        const data: IdMapper<Province> = { ...this.data };
        this.meta = payload.meta;
        this.ids = payload.datas.map((province: Province) => {
            data[province.id] = {
                ...province,
                isDeleting: false,
                isSaving: false,
                editable: false
            } as any;
            return province.id;
        });
        this.data = data;
        this.cachedData = cloneDeep(data);
    }

    @Mutation
    private MAP_PROVINCE_BY_COUNTRY_V21(payload: Province[]) {
        const data: IdMapper<Province> = cloneDeep(this.data);
        const mapIdsByProvinceId = cloneDeep(this.mapIdsByCountryId);
        payload.forEach((province: Province) => {
            data[province.id] = province;
            mapIdsByProvinceId[province.countryId] = union(mapIdsByProvinceId[province.countryId], [province.id]);
        });
        this.data = data;
        this.mapIdsByCountryId = mapIdsByProvinceId;
    }

    @Mutation
    private UPDATE_PROVINCE_PARAMS_V21(params: any) {
        this.params = { ...params };
    }

    @Mutation
    private UPDATE_DATA_PROVINCE_V21({ key, value }: any) {
        this.data[key] = value;
    }

    @Mutation
    private UPDATE_CACHED_DATA_PROVINCE_V21({ key, value }: any) {
        this.cachedData[key] = value;
    }

    @Mutation
    private SET_PROVINCE_V21(payload: Province) {
        this.ids = union([payload.id], this.ids);
        this.data = { ...this.data, [payload.id]: payload }
    }

    @Mutation
    private DELETE_PROVINCE_V21(payload: Province) {
        this.ids = this.ids.filter((id) => id != payload.id);
        delete this.data[payload.id];
    }

    @Mutation
    private CHANGE_LOADING_STATUS_PROVINCE_V21({key, value }: ChangeStatusProp) {
        set(this.loadingStatus, key, value);
    }

    @Action({ rawError: true })
    public async listProvincesV21(params: any = {}) {
        const { data } = await listProvinceV21(params);
        this.MAP_PROVINCES_V21(data);
        return data.datas;
    }

    @Action({ rawError: true })
    public async listProvincesByCountryV21(countryId = 1) {
        if (!this.loadingStatus[countryId] || this.loadingStatus[countryId] == 'waiting') {
            const params = { ...new ProvinceParams(), countryId, limit: 1000 };
            this.CHANGE_LOADING_STATUS_PROVINCE_V21({ key: `${countryId}`, value: "loading" });
            const { data } = await listProvinceV21(params);
            this.CHANGE_LOADING_STATUS_PROVINCE_V21({ key: `${countryId}`, value: "loaded" });
            this.MAP_PROVINCE_BY_COUNTRY_V21(data.datas);
            return data.datas;
        }
    }

    @Action({ rawError: true })
    public async createProvinceV21(payload: any) {
        const { data } = await createProvinceV21(payload);
        this.SET_PROVINCE_V21({ ...data.data, isDeleting: false, isSaving: false, editable: false });
        return data.data;
    }

    @Action({ rawError: true })
    public async editProvinceV21(payload: any) {
        const { data } = await editProvinceV21(payload);
        this.SET_PROVINCE_V21(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async deleteProvinceV21(id: number) {
        const { data } = await deleteProvinceV21(id);
        this.DELETE_PROVINCE_V21(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async paramsChangeProvinceV21(params: any) {
        this.UPDATE_PROVINCE_PARAMS_V21(params);
        await this.listProvincesV21(params);
    }

    @Action({ rawError: true })
    public updateDataProvinceV21({ key, value }: any) {
        this.UPDATE_DATA_PROVINCE_V21({ key, value });
    }

    @Action({ rawError: true })
    public updateCachedDataProvinceV21({ key, value }: any) {
        this.UPDATE_CACHED_DATA_PROVINCE_V21({ key, value });
    }

    @Action({ rawError: true })
    public async getProvincesForExcelV21({ countryIds, provinceSlugs }: any) {
        return;
    }
}

export const ProvinceStore = getModule(ProvinceModule)
